<template>
  <div
    data-cy="webhook-configuration"
    class="mx-4 my-5 rounded-sm bg-white py-4 shadow-xs"
    :class="{
      'pointer-events-none cursor-not-allowed opacity-50':
        isActiveTemplatePublished,
    }"
  >
    <div class="flex flex-col divide-y">
      <div
        data-cy="webhook-header"
        class="flex cursor-pointer items-center justify-between px-4"
        :class="{ 'pb-4': isOpen }"
        @click="toggleOpen"
      >
        <div class="text-headline">
          {{ $t("ORCHESTRATION.WEBHOOK_CONFIGURATION") }}
        </div>
        <dropdown-chevron :is-open="isOpen" class="mr-0!" />
      </div>
      <div v-if="isOpen" class="space-y-4 px-4 pt-4">
        <lf-input
          v-for="(url, index) in urls"
          :key="url.id"
          :data-cy="`webhook-url-input-${index}`"
          :name="`link-${url.id}`"
          :value="url.value"
          :disabled="isActiveTemplatePublished"
          class="url-input-wrapper h-10"
          :placeholder="`${$t('COMMON.LINK')} ${index + 1}`"
          no-margin
          @blur="handleInputUpdate(url, $event.target.value)"
        >
          <template #suffix>
            <clear-button
              v-if="!isActiveTemplatePublished"
              :data-cy="`webhook-clear-button-${index}`"
              class="clear-url absolute -top-3 -left-1 cursor-pointer text-gray-500 opacity-0"
              @click.prevent="removeUrl(url.id)"
            />
          </template>
        </lf-input>
        <button
          v-if="!isActiveTemplatePublished"
          data-cy="webhook-add-url-button"
          class="flex items-center space-x-2 font-medium text-primary"
          @click.prevent="addUrl"
        >
          <icon-base :icon="IconAdd" view-box="0 0 20 20" />
          <span>{{ $t("COMMON.ADD") }} {{ $t("COMMON.URL") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import { useStore } from "vuex";

import { v4 } from "uuid";

import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import IconAdd from "@/components/icons/deals/IconAdd.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ClearButton from "@/components/ui/buttons/ClearButton.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type { DoWebhook } from "@/models/orchestration";

interface Url {
  id: string;
  value: string;
}

const { webhooks, stepId, outcome } = defineProps<{
  webhooks?: DoWebhook[];
  stepId?: string;
  outcome?: string;
}>();

const { commit } = useStore();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const isOpen = ref(!!webhooks?.[0]?.urls?.length);

const getUrlsWithIds = () => {
  return webhooks?.[0]?.urls?.map((url) => ({ id: v4(), value: url })) || [];
};

const urls = ref<Url[]>([]);

const addUrl = () => {
  urls.value.push({ id: v4(), value: "" });
};

const removeUrl = (id: string) => {
  urls.value = urls.value.filter((url) => url.id !== id);
  updateWebhookForTheStep();
};

const handleInputUpdate = (url: Url, value: string) => {
  url.value = value;
  updateWebhookForTheStep();
};

const updateWebhookForTheStep = () => {
  commit("orchestration/setStepWebhooks", {
    urls: urls.value.map(({ value }) => value).filter(Boolean),
    stepId,
    outcome,
  });
};

const toggleOpen = () => (isOpen.value = !isOpen.value);

watch(
  () => webhooks,
  () => {
    urls.value = getUrlsWithIds();
  },
  { immediate: true }
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.url-input-wrapper:hover .clear-url {
  @apply opacity-100;
}
</style>
